import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import authService from "../services/authService";

const STEPS = { FORM: 1, VERIFY_EMAIL: 2, CONFIRM_CODE: 3 };

const RegisterPage = () => {
  const [step, setStep] = useState(STEPS.FORM);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const getErrorMessage = (errorCode) => {
    const messages = {
      EMAIL_EXISTS: t({
        en: "An account with this email already exists. Please log in instead.",
        de: "Ein Konto mit dieser E-Mail existiert bereits. Bitte melde dich an.",
      }),
      INVALID_INPUT: t({
        en: "Invalid email or password format.",
        de: "Ungültiges E-Mail- oder Passwortformat.",
      }),
      INVALID_CODE: t({
        en: "Invalid or expired confirmation code. Please try again.",
        de: "Ungültiger oder abgelaufener Bestätigungscode. Bitte versuche es erneut.",
      }),
      NETWORK_ERROR: t({
        en: "Network error - Please check your connection and try again.",
        de: "Netzwerkfehler - Bitte überprüfe deine Verbindung und versuche es erneut.",
      }),
      REGISTRATION_FAILED: t({
        en: "Registration failed. Please try again later.",
        de: "Registrierung fehlgeschlagen. Bitte versuche es später erneut.",
      }),
      CONFIRMATION_FAILED: t({
        en: "Confirmation failed. Please try again.",
        de: "Bestätigung fehlgeschlagen. Bitte versuche es erneut.",
      }),
    };
    return messages[errorCode] || errorCode;
  };

  // Step 1: Validate form locally and go to email verification
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(
        t({
          en: "Password must be at least 6 characters.",
          de: "Das Passwort muss mindestens 6 Zeichen lang sein.",
        })
      );
      return;
    }

    setStep(STEPS.VERIFY_EMAIL);
  };

  // Step 2: User confirms email is correct, call register API
  const handleConfirmEmail = async () => {
    setLoading(true);
    setError("");

    const result = await authService.register(email, password);

    if (result.success) {
      setStep(STEPS.CONFIRM_CODE);
    } else {
      setError(getErrorMessage(result.error));
    }
    setLoading(false);
  };

  // Step 3: Submit confirmation code, then auto-login
  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await authService.confirmRegistration(code, email);

    if (result.success) {
      // Auto-login with stored credentials
      const loginResult = await login(email, password);
      if (loginResult.success) {
        navigate("/", { replace: true });
      } else {
        // Auto-login failed, redirect to login page
        navigate("/login", { replace: true });
      }
    } else {
      setError(getErrorMessage(result.error));
    }
    setLoading(false);
  };

  const renderStepForm = () => (
    <form onSubmit={handleFormSubmit} autoComplete="on" className="space-y-6">
      <div>
        <label
          htmlFor="reg-email"
          className="block text-sm font-medium text-[#002855] mb-1"
        >
          {t({ en: "Email", de: "E-Mail" })}
        </label>
        <input
          id="reg-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] transition-colors"
          placeholder={t({
            en: "Enter your email address",
            de: "E-Mail-Adresse eingeben",
          })}
        />
      </div>

      <div>
        <label
          htmlFor="reg-password"
          className="block text-sm font-medium text-[#002855] mb-1"
        >
          {t({ en: "Password", de: "Passwort" })}
        </label>
        <input
          id="reg-password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] transition-colors"
          placeholder={t({
            en: "Choose a password (min. 6 characters)",
            de: "Passwort wählen (mind. 6 Zeichen)",
          })}
        />
      </div>

      {error && (
        <div className="text-[#DA291C] text-sm bg-red-50 border border-red-200 p-3 rounded-md">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!email || !password}
        className="w-full bg-[#002855] text-white py-2 px-4 rounded-md hover:bg-[#001a3d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {t({ en: "Register", de: "Registrieren" })}
      </button>
    </form>
  );

  const renderStepVerifyEmail = () => (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-4">
          {t({
            en: "Please verify your email address:",
            de: "Bitte überprüfe deine E-Mail-Adresse:",
          })}
        </p>
        <p className="text-lg font-semibold text-[#002855] bg-gray-50 p-3 rounded-md break-all">
          {email}
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
        <p className="text-sm text-amber-800">
          {t({
            en: "Email addresses are often entered incorrectly — the account cannot be activated then.",
            de: "E-Mail-Adressen werden oft falsch eingegeben — das Konto kann dann nicht aktiviert werden.",
          })}
        </p>
      </div>

      {error && (
        <div className="text-[#DA291C] text-sm bg-red-50 border border-red-200 p-3 rounded-md">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => {
            setError("");
            setStep(STEPS.FORM);
          }}
          className="flex-1 bg-gray-200 text-[#002855] py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
        >
          {t({ en: "Back", de: "Zurück" })}
        </button>
        <button
          type="button"
          onClick={handleConfirmEmail}
          disabled={loading}
          className="flex-1 bg-[#002855] text-white py-2 px-4 rounded-md hover:bg-[#001a3d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading
            ? t({ en: "Registering...", de: "Registrierung läuft..." })
            : t({ en: "Yes, email is correct", de: "Ja, E-Mail ist korrekt" })}
        </button>
      </div>
    </div>
  );

  const renderStepConfirmCode = () => (
    <form onSubmit={handleConfirmCode} className="space-y-6">
      <div className="text-center">
        <p className="text-sm text-gray-600">
          {t({
            en: "A confirmation code has been sent to:",
            de: "Ein Bestätigungscode wurde gesendet an:",
          })}
        </p>
        <p className="text-lg font-semibold text-[#002855] mt-1">{email}</p>
        <p className="text-xs text-gray-500 mt-2">
          {t({
            en: "Please also check your spam folder.",
            de: "Bitte überprüfe auch deinen Spam-Ordner.",
          })}
        </p>
      </div>

      <div>
        <label
          htmlFor="confirm-code"
          className="block text-sm font-medium text-[#002855] mb-1"
        >
          {t({ en: "Confirmation Code", de: "Bestätigungscode" })}
        </label>
        <input
          id="confirm-code"
          name="code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] transition-colors text-center text-xl tracking-widest"
          placeholder="000000"
        />
      </div>

      {error && (
        <div className="text-[#DA291C] text-sm bg-red-50 border border-red-200 p-3 rounded-md">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !code}
        className="w-full bg-[#002855] text-white py-2 px-4 rounded-md hover:bg-[#001a3d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading
          ? t({ en: "Confirming...", de: "Bestätigung läuft..." })
          : t({ en: "Confirm", de: "Bestätigen" })}
      </button>
    </form>
  );

  const stepTitles = {
    [STEPS.FORM]: t({
      en: "Create your burnair account",
      de: "Erstelle dein burnair Konto",
    }),
    [STEPS.VERIFY_EMAIL]: t({
      en: "Check your email address",
      de: "Überprüfe deine E-Mail-Adresse",
    }),
    [STEPS.CONFIRM_CODE]: t({
      en: "Enter confirmation code",
      de: "Bestätigungscode eingeben",
    }),
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div>
          <Link
            to="/"
            className="flex items-center text-[#00A3E0] hover:text-[#002855] mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t({ en: "Back to Academy", de: "Zurück zur Academy" })}
          </Link>

          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#002855]">
            {stepTitles[step]}
          </h2>
          {step === STEPS.FORM && (
            <p className="mt-2 text-center text-sm text-gray-500">
              {t({
                en: "This account works for both burnair Academy and burnair Map.",
                de: "Dieses Konto funktioniert sowohl für burnair Academy als auch für burnair Map.",
              })}
            </p>
          )}
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
          {step === STEPS.FORM && renderStepForm()}
          {step === STEPS.VERIFY_EMAIL && renderStepVerifyEmail()}
          {step === STEPS.CONFIRM_CODE && renderStepConfirmCode()}
        </div>

        {/* Login link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            {t({
              en: "Already have an account?",
              de: "Hast du bereits ein Konto?",
            })}{" "}
            <Link
              to="/login"
              className="text-[#00A3E0] hover:text-[#002855] font-medium transition-colors"
            >
              {t({ en: "Log in", de: "Anmelden" })}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
